

export const frontMatter = {
	sidebar_position: 3,
	license: 'republish',
	author: 'Jan van Steenbergen',
	origin: 'http://steen.free.fr/interslavic/phonology.html',
	description: 'Explore the phonology of Interslavic, a constructed language based on the Slavic languages, for a comprehensive linguistic comparison.',
	keywords: [
		'interslavic',
		'phonology',
		'slavic languages',
		'constructed language',
		'sound system',
		'vowels',
		'consonants',
		'phonotactics',
		'morphophonemic alternations'
	]
};
export const contentTitle = 'Phonology';
export {default as metadata} from '@site/.docusaurus/docusaurus-plugin-content-docs/default/site-docs-learn-phonology-md-492.json'
export const assets = {

};

import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const toc = [{
  "value": "Phoneme inventory",
  "id": "phoneme-inventory",
  "level": 2
}, {
  "value": "Vowels",
  "id": "vowels",
  "level": 2
}, {
  "value": "Consonants",
  "id": "consonants",
  "level": 2
}, {
  "value": "Hard and soft consonants",
  "id": "hard-and-soft-consonants",
  "level": 2
}, {
  "value": "Phonotactics",
  "id": "phonotactics",
  "level": 2
}, {
  "value": "Morphophonemic alternations",
  "id": "morphophonemic-alternations",
  "level": 2
}, {
  "value": "Palatalisation",
  "id": "palatalisation",
  "level": 3
}, {
  "value": "Iotation",
  "id": "iotation",
  "level": 3
}, {
  "value": "Palatalisation + iotation",
  "id": "palatalisation--iotation",
  "level": 3
}, {
  "value": "Overview",
  "id": "overview",
  "level": 4
}, {
  "value": "O ⇒ E",
  "id": "o--e",
  "level": 3
}, {
  "value": "Y ⇒ I/E",
  "id": "y--ie",
  "level": 3
}, {
  "value": "Ě ⇒ I",
  "id": "ě--i",
  "level": 3
}, {
  "value": "Fleeting o/e",
  "id": "fleeting-oe",
  "level": 3
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    abbr: "abbr",
    code: "code",
    em: "em",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    header: "header",
    li: "li",
    p: "p",
    span: "span",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {CustomAfterSection, CustomBeforeSection, IPA, MDPhonology1, MDPhonology2, MDPhonology3, TransliteratorElement} = _components;
  if (!CustomAfterSection) _missingMdxReference("CustomAfterSection", true);
  if (!CustomBeforeSection) _missingMdxReference("CustomBeforeSection", true);
  if (!IPA) _missingMdxReference("IPA", true);
  if (!MDPhonology1) _missingMdxReference("MDPhonology1", true);
  if (!MDPhonology2) _missingMdxReference("MDPhonology2", true);
  if (!MDPhonology3) _missingMdxReference("MDPhonology3", true);
  if (!TransliteratorElement) _missingMdxReference("TransliteratorElement", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.header, {
      children: _jsx(_components.h1, {
        id: "phonology",
        children: "Phonology"
      })
    }), "\n", _jsx(_components.h2, {
      id: "phoneme-inventory",
      children: "Phoneme inventory"
    }), "\n", _jsx(CustomBeforeSection, {}), "\n", _jsx(_components.p, {
      children: "Interslavic is neither a national language, nor does it try to emulate one.\nInstead, it is based on twelve national languages, each of them having its own phonology and its own corresponding orthography, tailored to fit its particular characteristics.\nIn general, it can be said that the further South you go, the smaller the phoneme (sound) inventory becomes.\nBoth spoken and written Interslavic should be in the very middle of them as much as possible."
    }), "\n", _jsxs(_components.p, {
      children: ["A high number of phonemes makes intuitive writing and pronouncing Interslavic harder for those who are used to fewer phonemes.\nA low number, on the other hand, makes it harder to link a particular character or sound to a phoneme in one's own language, and therefore has a negative effect on intelligibility.\nFor example, the ideal pronunciation of the word for „five” would be something like ", _jsx(IPA, {
        children: "pjætʲ"
      }), ".\nIf we write this as ", _jsx(TransliteratorElement, {
        children: "pęť"
      }), ", a Russian can easily recognise his own ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "ru",
        children: "пять"
      }), ", a Serb his own ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "sr",
        children: "pet"
      }), " and a Pole his own ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "pl",
        children: "pięć"
      }), "; but for a Serb, there is no way of knowing when his own ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t"
        })
      }), " become ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ę"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t́"
        })
      }), " without consulting a dictionary.\nOn the other hand, in a simplified scheme ", _jsx(TransliteratorElement, {
        children: "pet"
      }), " could also be misunderstood as Russian ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "ru",
        children: "петь"
      }), " „to sing” or Polish ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "pl",
        children: "pet"
      }), " „cigarette butt”.\nIn short: the easier we make it for the speaker/writer, the harder it becomes for the listener/reader, and vice versa."]
    }), "\n", _jsxs(_components.p, {
      children: ["To solve this dilemma, Interslavic has a basic set of phonemes that are present in all or a vast majority of the Slavic languages, more or less with the same phonetic values.\nIn addition, Interslavic also has a set of optional phonemes that link directly to Old Church Slavonic and refer to particular phonetic differences between languages.\nUsing the aforementioned word ", _jsx(TransliteratorElement, {
        children: "pęť"
      }), " as an example, Russian ", _jsx(IPA, {
        children: "pjætʲ"
      }), " and South Slavic ", _jsx(IPA, {
        children: "pɛt"
      }), " are simply two ways of pronouncing the very same word: the letters ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ę"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t́"
        })
      }), " indicate that their pronunciation varies among languages.\nThese additional phonemes can be written by means of an additional set of optional letters, part of the ", _jsx(_components.a, {
        href: "/learn/orthography#etymological-alphabet",
        children: "Interslavic etymological alphabet"
      }), " (previously known as ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        children: _jsx(_components.em, {
          children: "Naučny Medžuslovjansky"
        })
      }), ")."]
    }), "\n", _jsx(_components.p, {
      children: "The following charts give an overview of phonemes in Interslavic, based on their most average pronunciation.\nThe basic phonemes are shown in black, optional variations in gray."
    }), "\n", _jsx(_components.h2, {
      id: "vowels",
      children: "Vowels"
    }), "\n", _jsxs(_components.p, {
      children: ["Basic Interslavic has 7 vowel phonemes, five of which (", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "a"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "u"
        })
      }), ") have a rather uniform pronunciation, whereas the remaining two (", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ě"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), ") have a pronunciation that may differ between speakers."]
    }), "\n", _jsxs(_components.p, {
      children: ["In addition, there are 5 optional vowels (", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "å"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ė"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ę"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ȯ"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ų"
        })
      }), ") whose pronunciation may vary.\nThe diacritical marks are usually not written.\nIn flavourised versions of Interslavic however, ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "å"
        })
      }), " can be written and pronounced as ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ę"
        })
      }), " as ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ja"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ȯ"
        })
      }), " as ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " as ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), "."]
    }), "\n", _jsx(MDPhonology1, {}), "\n", _jsxs(_components.p, {
      children: ["Interslavic also has syllabic ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "r"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ŕ"
        })
      }), " (the latter belonging to the non-mandatory set).\nThis is the case when it is preceded by a consonant and ", _jsx(_components.em, {
        children: "not"
      }), " followed by a vowel.\nIt is pronounced with a schwa before it: ", _jsx(TransliteratorElement, {
        children: "trg"
      }), " ", _jsx(IPA, {
        children: "tərg"
      }), ", ", _jsx(TransliteratorElement, {
        children: "mŕtvy"
      }), " ", _jsx(IPA, {
        children: "mjərtvɪ"
      }), ", ", _jsx(TransliteratorElement, {
        children: "cukr"
      }), " ", _jsx(IPA, {
        children: "ʦukər"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "consonants",
      children: "Consonants"
    }), "\n", _jsx(_components.p, {
      children: "There are 23 basic consonants (including 3 affricates and 2 palatalised alveolars) with a more or less fixed pronunciation, as well as 7 optional consonants with a variable pronunciation:"
    }), "\n", _jsx(MDPhonology2, {}), "\n", _jsx(_components.h2, {
      id: "hard-and-soft-consonants",
      children: "Hard and soft consonants"
    }), "\n", _jsxs(_components.p, {
      children: ["Like all Slavic languages, Interslavic distinguishes between ", _jsx(_components.em, {
        children: "hard"
      }), " and ", _jsx(_components.em, {
        children: "soft"
      }), " consonants:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["The hard consonants are: the labials ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "p"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "b"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "f"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "v"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "m"
          })
        }), ", the hard dentals/alveolars ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "t"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "d"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "s"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "z"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "n"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "r"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "l"
          })
        }), ", and the velars ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "k"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "g"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "h"
          })
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["The soft consonants are: the postalveolars ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "š"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ž"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "č"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "dž"
          })
        }), ", the soft dentals/alveolars ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "lj"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "nj"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ŕ"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "t́"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "d́"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ś"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ź"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ć"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "đ"
          })
        }), ", and the palatal approximant ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["The affricate ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "c"
          })
        }), " ", _jsx(IPA, {
          children: "t͡s"
        }), " is pronounced hard, but in grammar it behaves like a soft consonant. Its voiced counterpart ", _jsx(IPA, {
          children: "d͡z"
        }), " does not occur in Interslavic."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Softening is the process of adding ", _jsx(IPA, {
        children: "ʲ"
      }), " to a consonant, resulting in a more palatal pronunciation.\nThe number of soft equivalents of hard consonants in the phoneme inventory varies greatly from one language to another.\nIn Interslavic only ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "lj"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "nj"
        })
      }), " are mandatory, the etymological alphabet also has ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t́"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "d́"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ś"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ź"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ŕ"
        })
      }), " (normally written ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "d"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "s"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "z"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "r"
        })
      }), "): the acute accent replaces Cyrillic ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "sla",
        children: _jsx(_components.code, {
          children: "ь"
        })
      }), ", which nowadays is used as a softener but used to be a vowel in the old days: an ultrashort ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "sla",
        children: _jsx(_components.code, {
          children: "ĭ"
        })
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "As can be seen from the table above, pronunciation of soft consonants varies.\nEast Slavic speakers are likely to pronounce them as softened dental or alveolar consonants, West Slavic speakers rather as palatal consonants.\nBoth pronuciations are equally correct, although the former is probably easier to understand for South Slavs."
    }), "\n", _jsxs(_components.p, {
      children: ["The soft consonants also include postalveolar ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "š"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ž"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "č"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "dž"
        })
      }), ", as well as the affricates ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ć"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "đ"
        })
      }), ".\nThe latter two are usually written and pronounced ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "č"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "dž"
        })
      }), ", too; the difference is of an etymological nature: ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ć"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "đ"
        })
      }), " are the iotated counterparts of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "d"
        })
      }), " (see below)."]
    }), "\n", _jsxs(_components.p, {
      children: ["Before ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ě"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ę"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ė"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ŕ"
        })
      }), ", a hard consonant can be softened or palatalised.\nThat is why a word like ", _jsx(TransliteratorElement, {
        children: "buditi"
      }), " is pronounced either ", _jsx(IPA, {
        children: "buditi"
      }), ", ", _jsx(IPA, {
        children: "budʲitʲi"
      }), " or ", _jsx(IPA, {
        children: "buɟici"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "phonotactics",
      children: "Phonotactics"
    }), "\n", _jsxs(_components.p, {
      children: ["Interslavic orthography is based on etymology and not on pronunciation, so that consonant clusters can arise that may appear inpronouncable for English speakers, for example ", _jsx(TransliteratorElement, {
        children: "vozvršenje"
      }), ".\nThe only limitations are related to combining certain vowels with certain consonants."]
    }), "\n", _jsx(_components.p, {
      children: "It is important to know the following:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Every syllable contains one of the following vowels: ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "a"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "å"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "e"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ę"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ė"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ě"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "i"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "o"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ȯ"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "u"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ų"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "y"
          })
        }), " or syllabic ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "r"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ŕ"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "å"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ę"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ė"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ě"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ȯ"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ų"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "y"
          })
        }), " and syllabic ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "r"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ŕ"
          })
        }), ": never occur word-initially or after a vowel"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "y"
          })
        }), ": can never follow a soft consonant"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ě"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ŕ"
          })
        }), ": always follow a hard consonant or ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "c"
          })
        }), " (they are redundant in cases like ", _jsx(TransliteratorElement, {
          children: "jěsti"
        }), " and ", _jsx(TransliteratorElement, {
          children: "čŕny"
        }), "), but can be pronounced in such way that they soften it"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "o"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ȯ"
          })
        }), ": rarely follow a soft consonant, except in loanwords like ", _jsx(TransliteratorElement, {
          children: "majonez"
        }), " and ", _jsx(TransliteratorElement, {
          children: "čokolada"
        })]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "å"
          })
        }), " is always preceded by a liquid (", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "r"
          })
        }), " or ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "l"
          })
        }), ") and followed by a consonant"]
      }), "\n", _jsxs(_components.li, {
        children: ["geminate consonants occur only as a result of prefixing (", _jsx(TransliteratorElement, {
          children: "od-dati"
        }), ", ", _jsx(TransliteratorElement, {
          children: "v-voz"
        }), ") or suffixing (", _jsx(TransliteratorElement, {
          children: "kon-ny"
        }), ", ", _jsx(TransliteratorElement, {
          children: "rus-sky"
        }), "), in rare cases in loanwords (", _jsx(TransliteratorElement, {
          children: "motto"
        }), ")."]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "morphophonemic-alternations",
      children: "Morphophonemic alternations"
    }), "\n", _jsx(_components.p, {
      children: "Inflection is kept as regular as possible.\nHowever, alternations like palatalisation and iotisation of consonants are an omnipresent phenomenon in Slavic.\nThey play a crucial role in both inflection and the word building process, and thus cannot be avoided even in the most simplified form of Interslavic—at least, if we want to avoid forms that come across as heavily artificial and unnatural."
    }), "\n", _jsx(_components.h3, {
      id: "palatalisation",
      children: "Palatalisation"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.em, {
        children: "Palatalisation"
      }), " means that under certain conditions the velar consonants ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "k"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "g"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "h"
        })
      }), " (as well as the dental affricate ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "c"
        })
      }), ") are changed to the postalveolar consonants ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "č"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ž"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "š"
        })
      }), ".\nThis happens in the following cases:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["before ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-e"
          })
        }), " in the vocative singular of masculine nouns: ", _jsxs(TransliteratorElement, {
          children: ["Bo", _jsx(_components.strong, {
            children: "g"
          })]
        }), " „God” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["Bo", _jsx(_components.strong, {
            children: "ž"
          }), "e"]
        }), ", ", _jsxs(TransliteratorElement, {
          children: ["hlåpė", _jsx(_components.strong, {
            children: "c"
          })]
        }), " „boy” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["hlåp", _jsx(_components.strong, {
            children: "č"
          }), "e"]
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["before ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-e"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-eš"
          })
        }), " etc. in the present tense of verbs: ", _jsxs(TransliteratorElement, {
          children: ["pe", _jsx(_components.strong, {
            children: "k"
          }), "-ti"]
        }), " „to bake” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["pe", _jsx(_components.strong, {
            children: "č"
          }), "eš"]
        }), ", ", _jsxs(TransliteratorElement, {
          children: ["mo", _jsx(_components.strong, {
            children: "g"
          }), "-ti"]
        }), " „can” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["mo", _jsx(_components.strong, {
            children: "ž"
          }), "eš"]
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["before ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-i-"
          })
        }), " in derived verbs: ", _jsx(TransliteratorElement, {
          children: "muka"
        }), " „torment” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["mu", _jsx(_components.strong, {
            children: "č"
          }), "iti"]
        }), " „to torment”, ", _jsxs(TransliteratorElement, {
          children: ["slu", _jsx(_components.strong, {
            children: "g"
          }), "a"]
        }), " „servant” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["slu", _jsx(_components.strong, {
            children: "ž"
          }), "iti"]
        }), " „to serve”"]
      }), "\n", _jsxs(_components.li, {
        children: ["before the suffixes ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-an(in)"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ba"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ec"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ica"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ina"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-išče"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-je"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ji"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-nik"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ny"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ok"
          })
        }), "/", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ka"
          })
        }), "/", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ko"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-sky"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-stvo"
          })
        }), ", etc.: ", _jsxs(TransliteratorElement, {
          children: ["rų", _jsx(_components.strong, {
            children: "k"
          }), "a"]
        }), " „hand” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["rų", _jsx(_components.strong, {
            children: "č"
          }), "ny"]
        }), " „manual”, ", _jsxs(TransliteratorElement, {
          children: ["mu", _jsx(_components.strong, {
            children: "h"
          }), "a"]
        }), " „fly” ⇒ ", _jsxs(TransliteratorElement, {
          children: ["mu", _jsx(_components.strong, {
            children: "š"
          }), "ka"]
        }), " „small fly”"]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Apart from the aforementioned vocative, palatalisation never occurs in the declension of nouns, adjectives and pronouns.\nThus: ", _jsx(_components.abbr, {
        title: "singular",
        children: "sg."
      }), " ", _jsx(TransliteratorElement, {
        children: "Čeh"
      }), " „Czech” ⇒ ", _jsx(_components.abbr, {
        title: "plural",
        children: "pl."
      }), " ", _jsx(TransliteratorElement, {
        children: "Čehi"
      }), " „Czechs”; ", _jsx(_components.abbr, {
        title: "singular",
        children: "sg."
      }), " ", _jsx(TransliteratorElement, {
        children: "dȯlgy"
      }), " „long” ⇒ ", _jsx(_components.abbr, {
        title: "plural",
        children: "pl."
      }), " ", _jsx(TransliteratorElement, {
        children: "dȯlgi"
      }), " (animate) or ", _jsx(TransliteratorElement, {
        children: "dȯlge"
      }), " (inanimate)."]
    }), "\n", _jsx(_components.h3, {
      id: "iotation",
      children: "Iotation"
    }), "\n", _jsxs(_components.p, {
      children: ["A different thing happens when a ", _jsx(_components.em, {
        children: "hard consonant"
      }), " is followed by ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j"
        })
      }), ".\nThe result is called ", _jsx(_components.em, {
        children: "iotation"
      }), ", which is not the same thing as softening.\nThe patterns according to which consonants interact with this ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j"
        })
      }), " vary from one language to another.\nSometimes, the result is the same as in the case of a softened consonant, sometimes it is different:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["The labials ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "p"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "b"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "m"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "f"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "v"
          })
        }), " are always hard. Before ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), ", East and South Slavic insert an ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "l"
          })
        }), " (", _jsxs(TransliteratorElement, {
          children: ["kup", _jsx(_components.strong, {
            children: "l"
          }), "ju"]
        }), ", ", _jsxs(TransliteratorElement, {
          children: ["sostav", _jsx(_components.strong, {
            children: "l"
          }), "jenie"]
        }), "), but in Interslavic we simply write ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "pj"
          })
        }), ", ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "vj"
          })
        }), " etc., both for reasons of clarity and regularity."]
      }), "\n", _jsxs(_components.li, {
        children: ["The velars ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "k"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "g"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "h"
          })
        }), ", when followed by ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), ", palatalise into ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "č"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ž"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "š"
          })
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["In the case of ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "l"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "n"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "r"
          })
        }), ", iotation gives the same result as softening: ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "lj"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "nj"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "rj"
          })
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["The combinations ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "s"
          })
        }), "+", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), " and ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "z"
          })
        }), "+", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), " become ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "š"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ž"
          })
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["The combinations ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "t"
          })
        }), "+", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), " and ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "d"
          })
        }), "+", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), " become ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "ć"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "đ"
          })
        }), " (normally written ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "č"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "dž"
          })
        }), ")."]
      }), "\n", _jsxs(_components.li, {
        children: ["In the sequences ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "sk"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "st"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "zg"
          })
        }), " ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "zd"
          })
        }), ", the ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "s"
          })
        }), " and ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "z"
          })
        }), " are iotated along with the following consonant, thus: ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "st"
          })
        }), "+", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "j"
          })
        }), " ⇒ ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "šć"
          })
        }), ", etc."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Iotation occurs mostly in ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-i-"
        })
      }), " class verbs:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["in the first person singular: ", _jsxs(TransliteratorElement, {
          children: ["pro", _jsx(_components.strong, {
            children: "s"
          }), "-iti"]
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["pro", _jsx(_components.strong, {
            children: "s"
          }), "-jų"]
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["pro", _jsx(_components.strong, {
            children: "š"
          }), "ų"]
        }), " (normally written ", _jsxs(TransliteratorElement, {
          children: ["pro", _jsx(_components.strong, {
            children: "š"
          }), "u"]
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: ["in the perfect passive participle: ", _jsxs(TransliteratorElement, {
          children: ["tra", _jsx(_components.strong, {
            children: "t"
          }), "-iti"]
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["tra", _jsx(_components.strong, {
            children: "t"
          }), "-jeny"]
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["tra", _jsx(_components.strong, {
            children: "ć"
          }), "eny"]
        }), " (normally written ", _jsxs(TransliteratorElement, {
          children: ["tra", _jsx(_components.strong, {
            children: "č"
          }), "eny"]
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: ["in the case of imperfective verbs derived from perfective verbs on ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-iti"
          })
        }), ": ", _jsxs(TransliteratorElement, {
          children: ["nagrå", _jsx(_components.strong, {
            children: "d"
          }), "-iti"]
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["nagra", _jsx(_components.strong, {
            children: "d"
          }), "-jati"]
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["nagra", _jsx(_components.strong, {
            children: "đ"
          }), "ati"]
        }), " (normally written ", _jsxs(TransliteratorElement, {
          children: ["nagra", _jsx(_components.strong, {
            children: "dž"
          }), "ati"]
        }), ")"]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Iotation does not occur when a word with initial ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j"
        })
      }), " is preceded by a prefix: ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "s"
        })
      }), "+", _jsx(TransliteratorElement, {
        children: "jesti"
      }), " becomes ", _jsxs(TransliteratorElement, {
        children: [_jsx(_components.strong, {
          children: "sj"
        }), "esti"]
      }), ", not ", _jsx(TransliteratorElement, {
        children: "šesti"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      id: "palatalisation--iotation",
      children: "Palatalisation + iotation"
    }), "\n", _jsxs(_components.p, {
      children: ["When a ", _jsx(_components.em, {
        children: "soft consonant"
      }), " is followed by ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j"
        })
      }), ", both the consonant and the glide remain untouched.\nAny changes are blocked by the softener, so to speak.\nIn etymological orthography ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ljj"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "njj"
        })
      }), " etc. are written as ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ľj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ńj"
        })
      }), ", to avoid gemination of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j"
        })
      }), ".\nLikewise, we write ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ŕj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "t́j"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "d́j"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "śj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "źj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "šj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "žj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "čj"
        })
      }), " as well.\nIn standard orthography we simply write ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "lj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "nj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "rj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "tj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "dj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "sj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "zj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "šj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "žj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "čj"
        })
      }), " in these cases.\nIf the stem ends in ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-j"
        })
      }), ", the following ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j-"
        })
      }), " is simply swallowed: ", _jsxs(TransliteratorElement, {
        children: ["dvo", _jsx(_components.strong, {
          children: "j"
        }), "-iti"]
      }), " ⇒ ", _jsxs(TransliteratorElement, {
        children: ["dvo", _jsx(_components.strong, {
          children: "j-j"
        }), "ų"]
      }), " ⇒ ", _jsxs(TransliteratorElement, {
        children: ["dvo", _jsx(_components.strong, {
          children: "j"
        }), "ų"]
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "This occurs in the following situations:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["with the palatalising suffix ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-’je"
          })
        }), " (indicating a place, for example): ", _jsx(TransliteratorElement, {
          children: "morje"
        }), "⇒ ", _jsx(TransliteratorElement, {
          children: "pri+mor+’je"
        }), " ⇒ ", _jsx(TransliteratorElement, {
          children: "primoŕje"
        }), " (normally written ", _jsx(TransliteratorElement, {
          children: "primorje"
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: ["in verbal nouns, where the ending ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-y"
          })
        }), " of the perfect passive participle is replaced with (palatalising) ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-’je"
          })
        }), ": ", _jsx(TransliteratorElement, {
          children: "dělati"
        }), " ⇒ ", _jsx(TransliteratorElement, {
          children: "dělańje"
        }), " (normally written ", _jsx(TransliteratorElement, {
          children: "dělanje"
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: ["with the palatalising possessive suffix ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ji"
          })
        }), ": ", _jsx(TransliteratorElement, {
          children: "Bog"
        }), " + ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "’ji"
          })
        }), " ⇒ ", _jsxs(TransliteratorElement, {
          children: ["Bo", _jsx(_components.strong, {
            children: "ž"
          }), "ji"]
        }), ", ", _jsx(TransliteratorElement, {
          children: "kot"
        }), " + ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "’ji"
          })
        }), " ⇒ ", _jsx(TransliteratorElement, {
          children: "koťji`"
        }), " (normally written ", _jsx(TransliteratorElement, {
          children: "kotji"
        }), ")"]
      }), "\n", _jsxs(_components.li, {
        children: ["in the instrumental singular of nouns of the ", _jsx(TransliteratorElement, {
          children: "kost"
        }), " type: ", _jsx(TransliteratorElement, {
          children: "kost"
        }), " + ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "’jų"
          })
        }), " ⇒ ", _jsx(TransliteratorElement, {
          children: "kosťjų"
        }), " (normally written ", _jsx(TransliteratorElement, {
          children: "kostju"
        }), ")"]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Complicated as this may seem, all this means in writing is that the suffixes ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-je"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ji"
        })
      }), " and the instrumental ending ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-jų"
        })
      }), " do not cause iotation, but only palatalisation of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "k"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "g"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "h"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "c"
        })
      }), "."]
    }), "\n", _jsx(_components.h4, {
      id: "overview",
      children: "Overview"
    }), "\n", _jsx(_components.p, {
      children: "The differences between softened, patalalised and iotated consonants are demonstrated in the following table (again, phonemes in gray are optional):"
    }), "\n", _jsx(MDPhonology3, {}), "\n", _jsxs(_components.h3, {
      id: "o--e",
      children: [_jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "O"
        })
      }), " ⇒ ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "E"
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Old Slavic used to have a peculiar intolerance of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), " following a soft consonant, and whenever such a sequence occurred because of an ending or a suffix, the ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), " was changed to ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), ".\nThis development has left its marks in all Slavic languages, although nowadays they differ as to the degree in which the is still applied.\nFor example, in Russian soft consonant + ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), " sequences are a rarity, while Polish has only some lexicalised remnants of the rule (e.g. ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "pl",
        children: "królewski"
      }), " „royal”, but: ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "pl",
        children: "królowa"
      }), " „queen”)."]
    }), "\n", _jsxs(_components.p, {
      children: ["This rule applies in Interslavic as well.\nThus, endings like ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-o"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ov"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-om"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ogo"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-oj"
        })
      }), " become ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-e"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ev"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-em"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ego"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ej"
        })
      }), " after a soft consonant.\nBecause of the ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), " rule, we have ", _jsx(TransliteratorElement, {
        children: "morje"
      }), " versus ", _jsx(TransliteratorElement, {
        children: "okno"
      }), ", ", _jsx(TransliteratorElement, {
        children: "krajev"
      }), " versus ", _jsx(TransliteratorElement, {
        children: "gradov"
      }), ", and ", _jsx(TransliteratorElement, {
        children: "čego"
      }), " versus ", _jsx(TransliteratorElement, {
        children: "kogo"
      }), ".\nThe same mechanism also works in combination with suffixes like ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ost"
        })
      }), ", ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ovati"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "-ovy"
        })
      }), ", for example: ", _jsx(TransliteratorElement, {
        children: "svěžesť"
      }), ", ", _jsx(TransliteratorElement, {
        children: "nočevati"
      }), "."]
    }), "\n", _jsxs(_components.h3, {
      id: "y--ie",
      children: [_jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "Y"
        })
      }), " ⇒ ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "I"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "E"
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["In South Slavic and Ukrainian, as well as in spoken Czech and Slovak, ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " have merged into one vowel.\nIn Interslavic, the pronunciation of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " may therefore be ", _jsx(IPA, {
        children: "i"
      }), ", ", _jsx(IPA, {
        children: "ɪ"
      }), ", ", _jsx(IPA, {
        children: "ɨ"
      }), " or anything in between.\nWhat matters, though, is that ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " can ", _jsx(_components.em, {
        children: "only"
      }), " occur after a hard consonant, and therefore not after a soft consonant (", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "š"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ž"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "č"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "dž"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "c"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "lj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "nj"
        })
      }), " ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "j"
        })
      }), "), after a vowel or word-initially.\nBecause ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " plays an prominent role in Interslavic inflection, most declensions have a hard and a soft version because of this limitation."]
    }), "\n", _jsxs(_components.p, {
      children: ["In the declension of adjectives and pronouns, case endings in ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " become ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), " after a soft consonant.\nFor that reason, we have adjectives like ", _jsxs(TransliteratorElement, {
        children: ["svěž-", _jsx(_components.strong, {
          children: "i"
        })]
      }), " along with adjectives like ", _jsxs(TransliteratorElement, {
        children: ["dobr-", _jsx(_components.strong, {
          children: "y"
        })]
      }), ", and pronominal forms like ", _jsxs(TransliteratorElement, {
        children: ["moj-", _jsx(_components.strong, {
          children: "ih"
        })]
      }), " along with forms like ", _jsxs(TransliteratorElement, {
        children: ["jegov-", _jsx(_components.strong, {
          children: "yh"
        })]
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["In noun declension, however, the soft counterpart of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " is always ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), ".\nFor example: ", _jsx(_components.abbr, {
        title: "masculine, plural",
        children: "m.pl."
      }), " ", _jsxs(TransliteratorElement, {
        children: ["dom-", _jsx(_components.strong, {
          children: "y"
        })]
      }), " versus ", _jsxs(TransliteratorElement, {
        children: ["kraj-", _jsx(_components.strong, {
          children: "e"
        })]
      }), ", ", _jsx(_components.abbr, {
        title: "feminine, plural",
        children: "f.pl."
      }), " ", _jsxs(TransliteratorElement, {
        children: ["žen-", _jsx(_components.strong, {
          children: "y"
        })]
      }), " versus ", _jsxs(TransliteratorElement, {
        children: ["zemj-", _jsx(_components.strong, {
          children: "e"
        })]
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["Because several Slavic languages do not distinguish between ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " at all, substituting all occurrences of ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " with ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "i"
        })
      }), " is an acceptable simplification in written Interslavic."]
    }), "\n", _jsxs(_components.h3, {
      id: "ě--i",
      children: [_jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "Ě"
        })
      }), " ⇒ ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "I"
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Just like ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), ", the phoneme ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ě"
        })
      }), " always follows a hard consonant.\nIn the dative and locative singular of feminine nouns, it becomes i after a soft consonant, i.e. it follows a pattern opposite to ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "y"
        })
      }), " ⇒ ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), ": ", _jsxs(TransliteratorElement, {
        children: ["žen-", _jsx(_components.strong, {
          children: "ě"
        })]
      }), ", but ", _jsxs(TransliteratorElement, {
        children: ["zemj-", _jsx(_components.strong, {
          children: "i"
        })]
      }), "."]
    }), "\n", _jsxs(_components.h3, {
      id: "fleeting-oe",
      children: ["Fleeting ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), "/", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["A characteristic feature of the Slavic languages is the existence of \"fleeting\" or \"movable\" vowels, referring to the phenomenon of vowels appearing and disappearing in a seemingly random manner, especially in certain inflected forms of nouns.\nThis is a result of different reflexes of the Common Slavic jers ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "sla",
        children: _jsx(_components.code, {
          children: "ъ"
        })
      }), " and ", _jsx(_components.span, {
        className: "notranslate",
        translate: "no",
        lang: "sla",
        children: _jsx(_components.code, {
          children: "ь"
        })
      }), ", which were lost in weak positions and vocalised to ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), " in strong positions.\nIn most cases this vowel appears in words that would otherwise end in a consonant cluster, and disappears when this cluster is followed by an ending."]
    }), "\n", _jsxs(_components.p, {
      children: ["Fleeting ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "o"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "e"
        })
      }), " (in the etymological alphabet marked with a dot: ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ȯ"
        })
      }), " and ", _jsx(TransliteratorElement, {
        children: _jsx(_components.code, {
          children: "ė"
        })
      }), ") appear especially in the following cases:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["in the nominative (and, in the case of inanimate nouns, the accusative) singular of masculine nouns, particularly those ending in ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ėc"
          })
        }), " and ", _jsx(TransliteratorElement, {
          children: _jsx(_components.code, {
            children: "-ȯk"
          })
        }), ", as well as a few other nouns:", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["ot", _jsx(_components.strong, {
                children: "ė"
              }), "c"]
            }), " „father” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, singular",
              children: "gen.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "otca"
            })]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["pěs", _jsx(_components.strong, {
                children: "ȯ"
              }), "k"]
            }), " „sand” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, singular",
              children: "gen.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "pěska"
            })]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["p", _jsx(_components.strong, {
                children: "ė"
              }), "s"]
            }), " „dog” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, singular",
              children: "gen.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "psa"
            })]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["s", _jsx(_components.strong, {
                children: "ȯ"
              }), "n"]
            }), " „dream” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, singular",
              children: "gen.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "sna"
            })]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["kr", _jsx(_components.strong, {
                children: "ȯ"
              }), "v"]
            }), " „blood” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, singular",
              children: "gen.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "krvi"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["in the genitive plural of feminine and neuter nouns that would otherwise end in a consonant cluster:", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "okno"
            }), " „window” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, plural",
              children: "gen.pl."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["ok", _jsx(_components.strong, {
                children: "ė"
              }), "n"]
            })]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "nominative, singular",
              children: "nom.sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "miska"
            }), " „bowl” ⇒ ", _jsx(_components.abbr, {
              title: "genitive, plural",
              children: "gen.pl."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["mis", _jsx(_components.strong, {
                children: "ȯ"
              }), "k"]
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["in the nominative singular masculine forms of certain pronouns:", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: [_jsx(_components.abbr, {
              title: "masculine, nominative, singular",
              children: "m.nom. sg."
            }), " ", _jsxs(TransliteratorElement, {
              children: ["v", _jsx(_components.strong, {
                children: "ė"
              }), "ś"]
            }), " „all, entire”, ", _jsx(_components.abbr, {
              title: "feminine, nominative, singular",
              children: "f.nom. sg."
            }), " ", _jsx(TransliteratorElement, {
              children: "vśa"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["before certain suffixes:", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: [_jsx(TransliteratorElement, {
              children: "piśmo"
            }), " „letter, script” ⇒ ", _jsxs(TransliteratorElement, {
              children: ["pis", _jsx(_components.strong, {
                children: "ė"
              }), "mny"]
            }), " „written, in writing”"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["the prepositions ", _jsx(TransliteratorElement, {
          children: "s"
        }), " and ", _jsx(TransliteratorElement, {
          children: "v"
        }), " when preceding certain pronouns or consonant clusters, or when used as a prefix before a vowel or a consonant cluster:", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: [_jsx(TransliteratorElement, {
              children: _jsx(_components.code, {
                children: "s"
              })
            }), " „with” + ", _jsx(TransliteratorElement, {
              children: "mnojų"
            }), " „me” (", _jsx(_components.abbr, {
              title: "instrumental, singular",
              children: "ins.sg."
            }), ") ⇒ ", _jsxs(TransliteratorElement, {
              children: ["s", _jsx(_components.strong, {
                children: "ȯ"
              }), " mnojų"]
            }), ","]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(TransliteratorElement, {
              children: _jsx(_components.code, {
                children: "v"
              })
            }), " „in” + ", _jsx(TransliteratorElement, {
              children: "vsih"
            }), " „all” (", _jsx(_components.abbr, {
              title: "locative, plural",
              children: "loc.pl."
            }), ") ⇒ ", _jsxs(TransliteratorElement, {
              children: ["v", _jsx(_components.strong, {
                children: "ȯ"
              }), " vsih"]
            }), ","]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(TransliteratorElement, {
              children: _jsx(_components.code, {
                children: "s-"
              })
            }), " + ", _jsx(TransliteratorElement, {
              children: "držati"
            }), " ⇒ ", _jsxs(TransliteratorElement, {
              children: ["s", _jsx(_components.strong, {
                children: "ȯ"
              }), "držati"]
            }), " „to contain”,"]
          }), "\n", _jsxs(_components.li, {
            children: [_jsx(TransliteratorElement, {
              children: _jsx(_components.code, {
                children: "v-"
              })
            }), " + ", _jsx(TransliteratorElement, {
              children: "idti"
            }), " ⇒ ", _jsxs(TransliteratorElement, {
              children: ["v", _jsx(_components.strong, {
                children: "ȯ"
              }), "jdti"]
            }), " „to enter”."]
          }), "\n"]
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(CustomAfterSection, {})]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}

